import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Handshake, Plus, Loader2, UserPlus, Save } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { plansApi, PageShell, PageHeader, StatusBadge, CARD, FIELD, LABEL, PRIMARY, GHOST, inr, dateShort, errMsg } from '../agencyPlans/shared';

const UserForm = ({ agencyId, onCreated }) => {
  const [f, setF] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await plansApi.post(`/admin/agencies/${agencyId}/users`, f); toast.success('Agency user created'); setF({ name: '', email: '', password: '' }); onCreated(); } catch (er) { toast.error(errMsg(er, 'Could not create user')); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="grid grid-cols-3 gap-2" data-testid="agency-user-form">
      <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" className={FIELD} data-testid="agency-user-name" />
      <input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Email" className={FIELD} data-testid="agency-user-email" />
      <input required minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="Password" className={FIELD} data-testid="agency-user-password" />
      <button disabled={busy} className={`${GHOST} col-span-3 justify-center`} data-testid="agency-user-create">{busy ? <Loader2 size={12} className="animate-spin" /> : <UserPlus size={12} />} Add user</button>
    </form>
  );
};

const AgencySheet = ({ agency, brands, onClose, onChanged }) => {
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSel(agency ? agency.brands.map((b) => b.id) : []); }, [agency]);
  const save = async () => {
    setBusy(true);
    try { await plansApi.put(`/admin/agencies/${agency.id}/brands`, { ids: sel }); toast.success('Brand assignments saved'); onChanged(); } catch (e) { toast.error(errMsg(e, 'Could not save')); } finally { setBusy(false); }
  };
  return (
    <Sheet open={!!agency} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="agency-detail-sheet">
        {agency && <div className="space-y-6">
          <SheetHeader><SheetTitle data-testid="agency-detail-name">{agency.agency_name}</SheetTitle><SheetDescription>{agency.plan_count} plans submitted</SheetDescription></SheetHeader>
          <section><div className={LABEL}>Assigned brands</div>
            <div className="space-y-1.5">{brands.map((b) => (
              <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`agency-brand-option-${b.id}`}>
                <input type="checkbox" className="accent-[#FF451A]" checked={sel.includes(b.id)} onChange={() => setSel(sel.includes(b.id) ? sel.filter((x) => x !== b.id) : [...sel, b.id])} data-testid={`agency-brand-check-${b.id}`} /> {b.brand_name}
              </label>))}
              {!brands.length && <p className="text-xs text-[#6B6B6B]">No brand accounts yet — create one in Brand Accounts.</p>}
            </div>
            <div className="flex justify-end mt-3"><button onClick={save} disabled={busy} className={PRIMARY} data-testid="agency-brands-save">{busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save assignments</button></div>
          </section>
          <section><div className={LABEL}>Users ({agency.users.length})</div>
            <div className="space-y-1 mb-3" data-testid="agency-users-list">{agency.users.map((u) => <div key={u.id} className="text-sm">{u.name} · <span className="text-[#6B6B6B]">{u.email}</span></div>)}{!agency.users.length && <p className="text-xs text-[#6B6B6B]">No users yet.</p>}</div>
            <UserForm agencyId={agency.id} onCreated={onChanged} />
          </section>
        </div>}
      </SheetContent>
    </Sheet>
  );
};

const NewAgency = ({ onCreated }) => {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const a = await plansApi.post('/admin/agencies', { agency_name: name }); setName(''); toast.success(`${a.agency_name} created`); onCreated(a.id); } catch (er) { toast.error(errMsg(er, 'Could not create agency')); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="flex gap-2" data-testid="agency-create-form">
      <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Agency name (e.g. Wavemaker)" className={`${FIELD} w-64`} data-testid="agency-create-name" />
      <button disabled={busy} className={PRIMARY} data-testid="agency-create-btn">{busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} New agency</button>
    </form>
  );
};

const TH = 'px-4 py-2.5 text-left text-[10px] font-semibold uppercase tracking-wider text-[#6B6B6B]';

export default function AdminAgencies() {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState(null);
  const [openId, setOpenId] = useState(null);
  const load = useCallback(() => {
    plansApi.get('/admin/agencies').then(setData).catch(() => setData({ agencies: [], brand_accounts: [] }));
    plansApi.get('').then((d) => setPlans(d.plans)).catch(() => setPlans([]));
  }, []);
  useEffect(load, [load]);
  const open = data?.agencies.find((a) => a.id === openId) || null;
  return (
    <PageShell testid="admin-agencies-page">
      <PageHeader icon={Handshake} title="Agencies" sub="Agency accounts, their users and which brands they can pitch to." action={<NewAgency onCreated={(id) => { load(); setOpenId(id); }} />} />
      {!data ? <Loader2 className="animate-spin" size={16} /> : (
        <div className={`${CARD} overflow-hidden`}>
          <table className="w-full" data-testid="agencies-table">
            <thead><tr className="bg-black/[0.02]">{['Agency', 'Users', 'Brands', 'Plans'].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
            <tbody>{data.agencies.map((a) => (
              <tr key={a.id} onClick={() => setOpenId(a.id)} className="border-t border-black/[0.06] cursor-pointer hover:bg-[#FF451A]/[0.03] transition-colors" data-testid={`agency-row-${a.id}`}>
                <td className="px-4 py-3 text-sm font-semibold">{a.agency_name}</td>
                <td className="px-4 py-3 text-xs text-[#4B5563]">{a.users.map((u) => u.email).join(', ') || '—'}</td>
                <td className="px-4 py-3 text-xs text-[#4B5563]">{a.brands.map((b) => b.brand_name).join(', ') || '—'}</td>
                <td className="px-4 py-3 text-sm font-mono">{a.plan_count}</td>
              </tr>))}
              {!data.agencies.length && <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-[#6B6B6B]">No agencies yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <section className={`${CARD} overflow-hidden`} data-testid="admin-all-plans">
        <div className="px-5 py-4 font-semibold">All submissions</div>
        <table className="w-full">
          <thead><tr className="bg-black/[0.02]">{['Campaign', 'Agency', 'Brand', 'Budget', 'Updated', 'Status'].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>{(plans || []).map((p) => (
            <tr key={p.id} className="border-t border-black/[0.06]" data-testid={`admin-plan-row-${p.id}`}>
              <td className="px-4 py-3 text-sm font-medium"><Link to={`/admin/agency-plans/${p.id}`} className="hover:text-[#FF451A] transition-colors" data-testid={`admin-plan-link-${p.id}`}>{p.campaign_name || 'Untitled'}</Link></td>
              <td className="px-4 py-3 text-xs">{p.agency_name}</td><td className="px-4 py-3 text-xs">{p.brand_name}</td>
              <td className="px-4 py-3 text-sm font-mono">{inr(p.total_budget)}</td><td className="px-4 py-3 text-xs text-[#6B6B6B]">{dateShort(p.updated_at)}</td>
              <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
            </tr>))}
            {plans && !plans.length && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-[#6B6B6B]">No submissions yet.</td></tr>}
          </tbody>
        </table>
      </section>
      <AgencySheet agency={open} brands={data?.brand_accounts || []} onClose={() => setOpenId(null)} onChanged={load} />
    </PageShell>
  );
}
